import React, { useState, useEffect, useCallback } from "react";
import {
    ShoppingCart,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    Package,
    Eye,
    X,
    LayoutGrid,
    List
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { ShopService } from "../services/user/ShopServices";
import { useCart } from "../context/CartContext";
import { useNotification } from "../context/NotificationContext";

export default function Shop() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { addToCart } = useCart();
    const { showNotification } = useNotification();

    // State
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error] = useState(null);
    const [viewMode, setViewMode] = useState("grid"); // grid or list

    // Pagination & Filters State
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1"));
    const [totalPages, setTotalPages] = useState(1);
    const [categoryType, setCategoryType] = useState(searchParams.get("type") || "");
    const [categoryId, setCategoryId] = useState(searchParams.get("category") || "");
    const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
    const [activeSearch, setActiveSearch] = useState(searchParams.get("q") || "");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    // Fetch Categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await ShopService.getCategories();
                setCategories(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed to fetch categories", err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch Products
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                per_page: 12,
                category_type: categoryType || undefined,
                category_id: categoryId || undefined,
                search: activeSearch || undefined
            };
            const data = await ShopService.getProducts(params);
            setProducts(data?.products || []);
            setTotalPages(data?.pages || 1);
        } catch (err) {
            showNotification({
                type: "failed",
                title: "Cart Error",
                message: "Unable to add item to cart. Please try again."
            });
        } finally {
            setLoading(false);
        }
    }, [currentPage, categoryType, categoryId, activeSearch, showNotification]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Handlers
    const handleCategoryChange = (type, id = "") => {
        setCategoryType(type);
        setCategoryId(id);
        setCurrentPage(1);

        const newParams = new URLSearchParams(searchParams);
        if (type) newParams.set("type", type); else newParams.delete("type");
        if (id) newParams.set("category", id); else newParams.delete("category");
        newParams.set("page", "1");
        setSearchParams(newParams);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setActiveSearch(searchTerm);
        setCurrentPage(1);
        const newParams = new URLSearchParams(searchParams);
        if (searchTerm) newParams.set("q", searchTerm); else newParams.delete("q");
        newParams.set("page", "1");
        setSearchParams(newParams);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", page.toString());
        setSearchParams(newParams);
    };

    const clearFilters = () => {
        setCategoryType("");
        setCategoryId("");
        setSearchTerm("");
        setActiveSearch("");
        setCurrentPage(1);
        setSearchParams({});
    };

    return (
        <div className="min-h-screen bg-white text-black font-sans">
            <SEO title="Official Store & Music Merch" description="Browse official vinyls, CDs, digital music, sheet music, and apparel." url="/shop" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">

                    {/* Page Header */}
                    <div className="mb-12 sm:mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col md:flex-row md:items-end justify-between gap-6"
                        >
                            <div>
                                <h1
                                    className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-4"
                                    style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                                >
                                    The Shop
                                </h1>
                                <p className="text-black font-medium font-serif italic text-lg max-w-xl">
                                    Browse Temidayo’s collection of official merchandise, limited edition vinyls, cd, digital releases, sheetmusic, and more.
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="relative group">
                                    <form onSubmit={handleSearchSubmit}>
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search items..."
                                            className="pl-12 pr-4 py-3 bg-white border border-black/5 rounded-2xl w-full md:w-64 focus:outline-none focus:border-jazz-brown transition-all duration-300 shadow-sm font-serif"
                                        />
                                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/80 group-focus-within:text-jazz-brown transition-colors" />
                                    </form>
                                </div>
                                <button
                                    onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                                    className="p-3 bg-white border border-black/5 rounded-2xl text-black/60 hover:text-black transition-colors shadow-sm hidden sm:block"
                                >
                                    {viewMode === "grid" ? <List size={20} /> : <LayoutGrid size={20} />}
                                </button>
                            </div>
                        </motion.div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-10">

                        {/* Sidebar / Filters */}
                        <aside className="w-full lg:w-72 lg:self-start lg:sticky lg:top-32 lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto custom-scrollbar pr-4 space-y-8 z-10">
                            <div>
                                <h3 className="text-xs font-bold tracking-[3px] uppercase text-black/60 mb-6 flex items-center gap-2">
                                    <Filter size={14} /> Filters
                                </h3>

                                <div className="space-y-6">
                                    {/* Category Types */}
                                    <div>
                                        <p className="text-sm font-bold text-black mb-4">Collection</p>
                                        <div className="flex flex-wrap lg:flex-col gap-2">
                                            {[
                                                { label: "All Items", value: "" },
                                                { label: "Music", value: "Music Content" },
                                                { label: "Merchandise", value: "Artist Merch" }
                                            ].map((type) => (
                                                <button
                                                    key={type.label}
                                                    onClick={() => handleCategoryChange(type.value)}
                                                    className={`px-4 py-2.5 rounded-xl text-sm transition-all duration-300 text-left border-2 ${categoryType === type.value
                                                        ? "bg-jazz-brown border-jazz-brown text-white shadow-[0_4px_12px_rgba(163,115,73,0.3)] translate-x-1 font-bold"
                                                        : "bg-white border-black/5 text-black/85 hover:border-jazz-brown/30 hover:bg-jazz-brown/5 font-semibold"
                                                        }`}
                                                >
                                                    {type.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Sub-categories */}
                                    {(categoryType || true) && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                        >
                                            <p className="text-sm font-bold text-black mb-4">Categories</p>
                                            <div className="flex flex-wrap lg:flex-col gap-2">
                                                {(categories || [])
                                                    .filter(c => !categoryType || c.category_type === categoryType)
                                                    .map((cat) => (
                                                        <button
                                                            key={cat.id}
                                                            onClick={() => handleCategoryChange(categoryType, cat.id)}
                                                            className={`px-4 py-2.5 rounded-xl text-sm transition-all duration-300 text-left border-2 ${categoryId === cat.id
                                                                ? "bg-jazz-brown border-jazz-brown text-white shadow-[0_4px_12px_rgba(163,115,73,0.3)] translate-x-1 font-bold"
                                                                : "bg-white border-black/5 text-black/85 hover:border-jazz-brown/30 hover:bg-jazz-brown/5 font-semibold"
                                                                }`}
                                                        >
                                                            {cat.name}
                                                        </button>
                                                    ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Active Filters Summary */}
                                    {(categoryType || categoryId || activeSearch) && (
                                        <div className="pt-6 border-t border-black/5">
                                            <button
                                                onClick={clearFilters}
                                                className="text-xs font-bold text-jazz-brown hover:underline underline-offset-4 flex items-center gap-2"
                                            >
                                                <X size={12} /> Clear all filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </aside>

                        {/* Product Grid */}
                        <div className="flex-1 min-h-[800px]">
                            {loading ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="aspect-[4/5] bg-gray-200 rounded-3xl mb-4" />
                                            <div className="h-4 bg-gray-200 rounded-full w-2/3 mb-2" />
                                            <div className="h-4 bg-gray-200 rounded-full w-1/3" />
                                        </div>
                                    ))}
                                </div>
                            ) : error ? (
                                <div className="text-center py-24 bg-white rounded-[2rem] border border-dashed border-black/10">
                                    <Package size={48} className="mx-auto text-black/10 mb-4" />
                                    <p className="text-black/60 font-serif italic text-lg">{error}</p>
                                </div>
                            ) : (products && products.length > 0) ? (
                                <>
                                    <div className={`grid ${viewMode === "grid"
                                        ? "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                                        : "grid-cols-1"
                                        } gap-4 sm:gap-6`}>
                                        <AnimatePresence mode="popLayout">
                                            {products.map((product) => (
                                                <ProductCard
                                                    key={product.id}
                                                    product={product}
                                                    viewMode={viewMode}
                                                    onAddToCart={addToCart}
                                                />
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="mt-16 flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="p-3 bg-white border border-black/5 rounded-2xl disabled:opacity-30 transition-all hover:bg-black hover:text-white shadow-sm"
                                            >
                                                <ChevronLeft size={20} />
                                            </button>

                                            {[...Array(totalPages)].map((_, i) => (
                                                <button
                                                    key={i + 1}
                                                    onClick={() => handlePageChange(i + 1)}
                                                    className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-sm transition-all duration-300 ${currentPage === i + 1
                                                        ? "bg-black text-white shadow-lg scale-110"
                                                        : "bg-white text-black/60 hover:bg-gray-100"
                                                        }`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}

                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="p-3 bg-white border border-black/5 rounded-2xl disabled:opacity-30 transition-all hover:bg-black hover:text-white shadow-sm"
                                            >
                                                <ChevronRight size={20} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="py-20 text-center bg-white rounded-3xl border border-black/5 shadow-sm">
                                    <Package size={48} className="mx-auto text-black/20 mb-6" />
                                    <h3 className="text-2xl font-serif italic mb-2">No items found</h3>
                                    <p className="text-black/85 font-medium text-sm mb-8">Try adjusting your filters or search term.</p>
                                    <button
                                        onClick={clearFilters}
                                        className="px-8 py-3 bg-black text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-jazz-brown transition-all duration-300"
                                    >
                                        Reset All Filters
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}

const ProductCard = ({ product, viewMode, onAddToCart }) => {
    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`group bg-white rounded-3xl border border-black/5 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 ${viewMode === "list" ? "flex flex-col sm:flex-row h-auto" : "flex flex-col h-full"
                }`}
        >
            {/* Image Container */}
            <div className={`relative overflow-hidden bg-gray-50 flex-shrink-0 ${viewMode === "list" ? "w-full sm:w-64 aspect-square" : "aspect-[4/5]"
                }`}>
                <img
                    src={product.image || "/api/placeholder/400/500"}
                    alt={product.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                />

                {/* Overlays */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {product.is_popular && (
                        <span className="bg-jazz-brown text-white text-[9px] font-bold px-3 py-1.5 rounded-full tracking-widest uppercase shadow-lg">
                            Popular
                        </span>
                    )}
                    {product.stock_quantity === 0 && product.category_type !== "Music Content" && (
                        <span className="bg-red-500 text-white text-[9px] font-bold px-3 py-1.5 rounded-full tracking-widest uppercase shadow-lg">
                            Sold Out
                        </span>
                    )}
                </div>

                {/* Quick Actions */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 gap-3">
                    <Link
                        to={`/shop/product/${product.slug}`}
                        className="p-4 bg-white text-black rounded-full hover:bg-black hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 shadow-xl"
                    >
                        <Eye size={20} />
                    </Link>
                    <button
                        onClick={() => onAddToCart(product)}
                        disabled={product.stock_quantity === 0 && product.category_type !== "Music Content"}
                        className="p-4 bg-white text-black rounded-full hover:bg-jazz-brown hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-75 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ShoppingCart size={20} />
                    </button>
                </div>
            </div>

            {/* Content Container */}
            <div className="p-6 flex flex-col flex-1">
                <div className="mb-4">
                    <div className="flex justify-between items-start gap-2 mb-2">
                        <span className="text-[10px] font-bold text-jazz-brown tracking-[2px] uppercase">
                            {product.category_name}
                        </span>
                        <span className="text-lg font-bold text-black font-serif">
                            ${Number(product.price).toFixed(2)}
                        </span>
                    </div>
                    <Link to={`/shop/product/${product.slug}`}>
                        <h3 className="text-xl font-medium text-black group-hover:text-jazz-brown transition-colors duration-300 leading-tight mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            {product.name}
                        </h3>
                    </Link>
                    <p className="text-black text-xs font-medium line-clamp-2 leading-relaxed">
                        {product.description}
                    </p>
                </div>

                <div className="mt-auto pt-6 border-t border-black/5 flex items-center justify-between">
                    <button
                        onClick={() => onAddToCart(product)}
                        disabled={product.stock_quantity === 0 && product.category_type !== "Music Content"}
                        className="text-[10px] font-bold uppercase tracking-[2px] text-black group-hover:text-jazz-brown flex items-center gap-2 transition-colors disabled:opacity-30"
                    >
                        <ShoppingCart size={14} /> Add to Cart
                    </button>
                    <Link
                        to={`/shop/product/${product.slug}`}
                        className="text-[10px] font-bold uppercase tracking-[2px] text-black/80 hover:text-black transition-colors"
                    >
                        Details
                    </Link>
                </div>
            </div>
        </motion.div>
    );
};
